import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  MessageSquare,
  Bot,
  Workflow,
  Activity,
  Database,
  Blocks,
  Lightbulb,
  ChevronLeft,
  ChevronRight,
  TerminalSquare,
  BookOpen,
} from 'lucide-react';
import { Logo } from '../../common/Logo';
import { UserMenu } from '../../common/UserMenu';
import { Tooltip } from '../../ui';
import { useLocalStorage } from '../../../hooks';
import { useAuth } from '../../../context';
import styles from './MainNav.module.css';

interface NavItemConfig {
  id: string;
  icon: React.ReactNode;
  label: string;
  path: string;
  badge?: string;
}

// Primary navigation items
const primaryNavItems: NavItemConfig[] = [
  { id: 'chats', icon: <MessageSquare size={18} />, label: 'Chats', path: '/' },
  { id: 'assistants', icon: <Bot size={18} />, label: 'Assistants', path: '/assistants' },
  { id: 'skills', icon: <Lightbulb size={18} />, label: 'Skills', path: '/skills' },
  { id: 'workflows', icon: <Workflow size={18} />, label: 'Workflows', path: '/workflows' },
  { id: 'developer-agents', icon: <TerminalSquare size={18} />, label: 'Developer Agents', path: '/developer-agents' },
  { id: 'brownfield', icon: <Database size={18} />, label: 'App Intelligence', path: '/brownfield' },
];

// Secondary navigation items
const secondaryNavItems: NavItemConfig[] = [
  { id: 'knowledge', icon: <BookOpen size={18} />, label: 'Knowledge Hub', path: '/knowledge' },
  { id: 'analytics', icon: <Activity size={18} />, label: 'Analytics', path: '/analytics' },
];

export interface MainNavProps {
  user?: {
    name: string;
    avatar?: string;
  };
}

export const MainNav: React.FC<MainNavProps> = ({ user: propUser }) => {
  const location = useLocation();
  const { value: navCollapsed, setValue: setNavCollapsed } = useLocalStorage('mainnav-collapsed', false);
  const { user: authUser } = useAuth();

  // Use auth context user, fall back to prop user
  const user = authUser
    ? { name: authUser.name, avatar: authUser.avatar }
    : propUser;

  const isActive = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const toggleNav = () => setNavCollapsed(!navCollapsed);

  const renderNavItem = (item: NavItemConfig) => {
    const link = (
      <NavLink
        to={item.path}
        className={`${styles.navItem} ${isActive(item.path) ? styles.active : ''}`}
      >
        <span className={styles.navIcon}>{item.icon}</span>
        {!navCollapsed && <span className={styles.navLabel}>{item.label}</span>}
        {!navCollapsed && item.badge && <span className={styles.navBadge}>{item.badge}</span>}
      </NavLink>
    );

    if (navCollapsed) {
      return (
        <Tooltip key={item.id} content={item.label} position="right">
          {link}
        </Tooltip>
      );
    }

    return <React.Fragment key={item.id}>{link}</React.Fragment>;
  };

  return (
    <nav className={`${styles.mainNav} ${navCollapsed ? styles.collapsed : ''}`}>
      {/* Brand */}
      <div className={styles.brandSection}>
        <Logo size="sm" />
        {!navCollapsed && <span className={styles.brandName}>BrainIQ</span>}
      </div>

      {/* Navigation */}
      <div className={styles.navItems}>
        <div className={styles.navGroup}>
          {primaryNavItems.map(renderNavItem)}
        </div>

        <div className={styles.separator} />

        <div className={styles.navGroup}>
          {secondaryNavItems.map(renderNavItem)}
        </div>
      </div>

      {/* Bottom section */}
      <div className={styles.bottomSection}>
        {user && (
          <div className={styles.userSection}>
            <UserMenu user={{ ...user, username: user.name.toLowerCase().replace(' ', '') }} />
            {!navCollapsed && <span className={styles.userName}>{user.name}</span>}
          </div>
        )}

        <button
          type="button"
          className={styles.collapseButton}
          onClick={toggleNav}
          aria-label={navCollapsed ? 'Expand menu' : 'Collapse menu'}
        >
          {navCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          {!navCollapsed && <span>Collapse</span>}
        </button>
      </div>
    </nav>
  );
};

export default MainNav;
